<template>
  <div>
    <!-- Toggle button for mobile -->
    <button
      v-if="isMobile && !mobileOpen"
      @click="toggleSidebar"
      class="position-fixed top-0 start-0 m-3 z-3 btn btn-sm btn-light d-lg-none"
      aria-label="Toggle menu"
      style="z-index: 10001;"
    >
      <component :is="MenuIcon" class="h-5 w-5" />
    </button>

    <!-- Sidebar panel -->
    <div
      :class="['d-flex flex-column bg-white shadow border-end', sidebarClasses]"
      :style="{
        minHeight: '100vh',
        zIndex: 9999,
        position: isMobile ? 'fixed' : 'sticky',
        top: isMobile ? '0' : '0'
      }"
    >
      <div class="d-flex justify-content-between align-items-center border-bottom px-3 py-2">
        <h1 v-if="!collapsed" class="h-6 mb-0 fw-bold">AMD Transportation</h1>
        <button @click="toggleSidebar" class="btn btn-sm">
          <component :is="isMobile ? (mobileOpen ? XIcon : MenuIcon) : (collapsed ? MenuIcon : XIcon)" class="h-5 w-5" />
        </button>
      </div>

      <!-- Dynamic Nav Items -->
      <ul class="list-unstyled m-0 p-3">
        <li v-for="item in filteredNavItems" :key="item.name" class="mb-2">
          <router-link
            :to="item.path"
            class="d-flex align-items-center gap-2 px-3 py-2 rounded"
            :class="{
              'bg-primary text-white fw-medium': $route.path === item.path,
              'text-secondary': $route.path !== item.path
            }"
          >
            <component :is="item.icon" class="h-5 w-5 flex-shrink-0" />
            <span v-if="!collapsed">{{ item.name }}</span>
          </router-link>
        </li>
      </ul>

      <!-- Credits Section -->
      <div class="mt-auto px-3 pb-3">
        <!-- <div class="mb-2">
          <div class="d-flex align-items-center justify-content-between px-3 py-2 border rounded-3 bg-light">
            <span v-if="!collapsed" class="text-muted small">Credits</span>
            <span class="fw-semibold">{{ credit }}</span>
            <button v-if="!collapsed"
              class="btn btn-sm btn-outline-secondary ms-2 d-flex align-items-center"
              @click="fetchCredits"
              :disabled="loadingCredits"
              title="Refresh credits"
            >
              <span v-if="!loadingCredits">↻</span>
              <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            </button>
          </div>
        </div> -->
        <button
          class="btn btn-outline-danger w-100 d-flex align-items-center gap-2"
          @click="handleLogout"
        >
          <component :is="LogOut" class="h-5 w-5 flex-shrink-0" />
          <span v-if="!collapsed">Log Out</span>
        </button>
      </div>
    </div>

    <!-- Mobile Backdrop -->
    <div
      v-if="isMobile && mobileOpen"
      class="position-fixed top-0 start-0 w-100 h-100"
      style="background: rgba(0,0,0,.35); z-index: 9998;"
      @click="toggleSidebar"
      aria-hidden="true"
    ></div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { mapGetters } from 'vuex'
import { 
  Users, Menu as MenuIcon, X as XIcon, LogOut, Settings, 
  CalendarCheck,  Box,Car,  Coins, BriefcaseBusiness,ReceiptText, Mail
} from 'lucide-vue-next'
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth'
import { useToast } from 'vue-toastification'
import router from "@/router"

export default {
  name: 'Sidebar',
  data() {
    return {
      userID: "",
    }
  },
  setup() {
    const toast = useToast()
    const route = useRoute()
    const isMobile = ref(window.innerWidth < 1024)
    const collapsed = ref(false)
    const mobileOpen = ref(false)

    const updateIsMobile = () => {
      isMobile.value = window.innerWidth < 1024
      if (!isMobile.value) mobileOpen.value = false
    }

    onMounted(() => {
      window.addEventListener('resize', updateIsMobile)
      updateIsMobile()
    })
    onUnmounted(() => {
      window.removeEventListener('resize', updateIsMobile)
    })

    watch(() => route.path, () => {
      if (isMobile.value && mobileOpen.value) mobileOpen.value = false
    })

    const toggleSidebar = () => {
      if (isMobile.value) {
        mobileOpen.value = !mobileOpen.value
      } else {
        collapsed.value = !collapsed.value
      }
    }

    const sidebarClasses = computed(() => {
      if (!isMobile.value) return collapsed.value ? 'w-85px' : 'w-250px'
      return mobileOpen.value ? 'position-fixed top-0 start-0 h-100' : 'd-none'
    })

    // Full list of nav items
    const navItems = [
      { name: 'Agent', key: 'CRM', icon: Users, path: '/crm' },
      { name: 'Resources', key: "Inventory", icon: Box, path: '/Inventory' }, // Always visible
      { name: 'Job Management', key: 'JobManagement', icon: BriefcaseBusiness, path: '/JobManagement' },
      { name: 'Job Emails', key: 'EmailInbox', icon: Mail, path: '/EmailInbox' }, // admin only
      { name: 'Driver Job', key: 'DriverJob', icon: BriefcaseBusiness, path: '/DriverJob' },
      { name: 'Booking', key: 'Booking', icon: Car, path: '/Booking' },
      { name: 'Voucher', key: 'Voucher', icon: ReceiptText, path: '/DriverVoucherList' },
    ]

    return {
      navItems,
      collapsed,
      isMobile,
      mobileOpen,
      toggleSidebar,
      sidebarClasses,
      MenuIcon,
      XIcon,
      LogOut,
      Settings,
      toast,
      CalendarCheck,
      Coins,
      ReceiptText
    }
  },
  computed: {
    ...mapGetters(["getSystemAccess"]),
    /**
     * Filters nav items dynamically based on Vuex systemAccess
     */
    filteredNavItems() {
      const access = this.$store.getters.getSystemAccess || {}; // fallback if Vuex empty
      console.log("🔹 Access claims:", access);

      return this.navItems.filter(item => {
        // Always visible if no key
        if (!item.key) return true;

        // 🔹 Admin gets all items
        if (access.admin === true) return item.key !== "DriverJob"

        // 🔹 Driver only sees Job Management
        if (access.driver === true) return item.key === "DriverJob" || item.key === "Voucher" ;

        // 🔹 Client only sees Booking
        if (access.client === true) return item.key === "Booking";

        // Default fallback — if item-specific access flags exist
        return access[item.key] === true;
      });
    }




  },
  methods: {
    handleLogout() {
      const auth = getAuth()
      signOut(auth)
        .then(() => this.$router.push('/'))
        .catch(() => {})
    },
    async getAuthHeader() {
      const user = getAuth().currentUser
      if (!user) throw new Error('Not logged in')
      const token = await user.getIdToken()
      return { Authorization: token }
    },
    async checkAuthState() {
      const auth = getAuth()
      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            try {
              const idToken = await user.getIdToken()
              this.token = idToken
              resolve()
            } catch (error) {
              reject(error)
            }
          } else {
            router.push("/")
            reject(new Error("No user logged in"))
          }
        })
      })
    },
  },
  async mounted() {
    this.userID = this.$store.getters.getUserId
    await this.checkAuthState()
  }
}
</script>

<style scoped>
/* desktop widths */
.w-85px { width: 85px !important; }
.w-250px { width: 250px !important; }

/* fixed mobile drawer */
:deep(.position-fixed.top-0.start-0.h-100) {
  width: 260px;
  background: #fff;
}
</style>
